<template>
  <div>
    <v-dialog v-model="signatureConfirmDialog" max-width="400">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          style="width: 100%"
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Gostaria de assinar como Técnico Executor?') }}
          </div>

          <div class="d-flex justify-center">
            <v-radio-group v-model="option" dark>
              <v-radio :value="0">
                <template v-slot:label>
                  <span class="grey--text text-body-2">
                    {{ $t('Usar assinatura do usuário logado') }}
                  </span>
                </template>
              </v-radio>
              <v-radio :value="1">
                <template v-slot:label>
                  <span class="grey--text text-body-2">
                    {{ $t('Assinar via touchscreen') }}
                  </span>
                </template>
              </v-radio>
              <v-radio :value="2">
                <template v-slot:label>
                  <span class="grey--text text-body-2">
                    {{ $t('Usar assinatura do responsável pela O.S.') }}
                  </span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <v-expand-transition>
            <div v-if="option == 1">
              <SignatureScreen
                v-model="checklist.executorName"
                @save="validateChecklist"
              />
            </div>
          </v-expand-transition>

          <v-btn
            v-if="option == 0 || option == 2"
            :disabled="loading"
            depressed
            class="ma-1"
            color="grey"
            @click="signatureConfirmDialog = false"
          >
            {{ $t('Não') }}
          </v-btn>

          <v-btn
            v-if="option == 0 || option == 2"
            :loading="loading"
            class="ma-1"
            depressed
            color="#B14949"
            @click="validateChecklist"
          >
            {{ $t('Sim') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <v-card class="card-pad d-flex align-center mb-4" height="75">
      <v-btn
        depressed
        small
        text
        color="grey"
        @click="$router.push('/inspectionRoutineChecklist')"
      >
        <v-icon x-small>mdi-arrow-left-bold</v-icon>
        {{ $t('Voltar') }}
      </v-btn>
    </v-card>

    <v-card
      class="card-pad error pa-0 pa-2 my-4"
      v-if="checklist.isValidatorSigned"
    >
      <v-row class="ma-0 px-4" dense justify="end">
        <v-col class="d-flex align-center my-2">
          <strong class="white--text">{{ $t('Checklist Validado') }}</strong>
        </v-col>
      </v-row>
    </v-card>

    <v-card
      class="card-pad error pa-0 pa-2 my-4"
      v-if="
        checklist.serviceOrder &&
        checklist.serviceOrder.closeServiceOrderDateTime
      "
    >
      <v-row class="ma-0 px-4" dense justify="end">
        <v-col class="d-flex align-center my-2">
          <strong class="white--text">{{
            $t('Ordem de serviço fechada')
          }}</strong>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="card-container mb-4">
      <strong class="ml-2">Checklist de {{ $t('Rotina de Inspeção') }}</strong>
      <v-form ref="form">
        <v-row class="ma-0 mt-4" dense>
          <v-col>
            <label class="text--caption ml-1">
              {{ $t('Data da execução') }}
            </label>
            <v-menu
              ref="menuExecutionDate"
              v-model="menuExecutionDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  hide-details="auto"
                  v-model="executionDateFormatted"
                  v-mask="['##/##/####']"
                  :placeholder="$t('Data')"
                  prepend-inner-icon="mdi-calendar"
                  @blur="updateExecutionDateFromString"
                  v-bind="attrs"
                  v-on="on"
                  autocomplete="off"
                  outlined
                  dense
                  :disabled="
                    checklist.isValidatorSigned ||
                    (checklist.serviceOrder &&
                      checklist.serviceOrder.closeServiceOrderDateTime)
                  "
                  :error="executionDateTimeError"
                  class="primary-input"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="executionDate"
                min="1900-01-01"
                @change="saveExecutionDate"
                no-title
                locale="pt"
              ></v-date-picker>
            </v-menu>
            <span class="date-span-error" v-if="executionDateTimeError">
              {{ $t('Data da execução é requerida') }}
            </span>
          </v-col>
          <v-col cols="12">
            <label class="text--caption ml-1">{{ $t('Rodapé') }}</label>
            <v-textarea
              dense
              :rules="[(v) => !!v || 'Rodapé obrigatório']"
              v-model="checklist.footnote"
              outlined
              :disabled="
                checklist.isValidatorSigned ||
                (checklist.serviceOrder &&
                  checklist.serviceOrder.closeServiceOrderDateTime)
              "
              class="primary-input"
              :placeholder="$t('Digite o rodapé')"
              hide-details="auto"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-textarea>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <v-card class="card-container mb-4">
      <strong class="ml-1">Itens do checklist</strong>
      <v-data-table
        class="main-table mt-4"
        :loading-text="$t('Carregando...')"
        :no-data-text="$t('Nenhum item encontrado')"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="0"
        :headers="headers"
        :items="checklist.answerTemplate.items"
        disable-filtering
        group-by="equipment.name"
        disable-pagination
      >
        <template #header="{ props, on }">
          <thead>
            <tr>
              <th
                class="text-center"
                v-for="header in props.headers"
                :key="header.value"
              >
                <v-simple-checkbox
                  v-if="header.value === 'data-table-select'"
                  v-model="props.everyItem"
                  :indeterminate="props.someItems && !props.everyItem"
                  color="purple"
                  @input="on['toggle-select-all']"
                ></v-simple-checkbox>

                <div>
                  <v-hover v-slot="{ hover }">
                    <div>
                      <span>{{ $t(header.text) }}</span>
                      <v-icon
                        v-if="
                          hover &&
                          header.value != 'data-table-select' &&
                          header.value != 'actions'
                        "
                        color="white"
                        :disabled="
                          checklist.isValidatorSigned ||
                          (checklist.serviceOrder &&
                            checklist.serviceOrder.closeServiceOrderDateTime)
                        "
                        class="mx-2"
                        @click="on.sort(header.value)"
                      >
                        {{
                          props.options.sortBy.includes(header.value)
                            ? `mdi-chevron-down`
                            : `mdi-chevron-up`
                        }}
                      </v-icon>
                    </div>
                  </v-hover>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #[`group.header`]="{ group }">
          <th
            colspan="8"
            v-if="
              checklist.answerTemplate.type == 1 &&
              checklist.answerTemplate.withoutEquipment == false
            "
          >
            <span class="ml-6"> {{ group }} </span>
          </th>
        </template>

        <template #[`item.checklistItemYes`]="{ item }">
          <v-icon
            @click="
              item.response == 0 ? (item.response = null) : (item.response = 0)
            "
            :disabled="
              checklist.isValidatorSigned ||
              (checklist.serviceOrder &&
                checklist.serviceOrder.closeServiceOrderDateTime)
            "
            :color="item.response == 0 ? '#BBCF20' : ''"
          >
            {{
              item.response == 0
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>
        <template #[`item.checklistItemNo`]="{ item }">
          <v-icon
            @click="
              item.response == 1 ? (item.response = null) : (item.response = 1)
            "
            :disabled="
              checklist.isValidatorSigned ||
              (checklist.serviceOrder &&
                checklist.serviceOrder.closeServiceOrderDateTime)
            "
            :color="item.response == 1 ? '#B14949' : ''"
          >
            {{
              item.response == 1
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>

        <template #[`item.checklistItemNotApplicaton`]="{ item }">
          <v-icon
            @click="
              item.response == 2 ? (item.response = null) : (item.response = 2)
            "
            :disabled="
              checklist.isValidatorSigned ||
              (checklist.serviceOrder &&
                checklist.serviceOrder.closeServiceOrderDateTime)
            "
            :color="item.response == 2 ? '#426483' : ''"
          >
            {{
              item.response == 2
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>

        <template #[`item.observation`]="{ item }">
          <v-textarea
            hide-details="auto"
            class="text-area-input mt-3"
            :disabled="
              checklist.isValidatorSigned ||
              (checklist.serviceOrder &&
                checklist.serviceOrder.closeServiceOrderDateTime)
            "
            dense
            :placeholder="$t('Digite aqui observações sobre sua resposta...')"
            no-resize
            rows="3"
            v-model="item.observation"
            outlined
          ></v-textarea>
        </template>
      </v-data-table>
    </v-card>

    <v-card class="card-pad d-flex mb-4">
      <v-btn
        class="primary-button"
        depressed
        @click="$router.push('/serviceOrders')"
      >
        Cancelar
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        class="secondary-button mr-0"
        depressed
        :disabled="
          checklist.isValidatorSigned ||
          (checklist.serviceOrder &&
            checklist.serviceOrder.closeServiceOrderDateTime)
        "
        @click="checklist.isExecutingSigned ? submit() : openSignatureModal()"
      >
        {{ $t('Salvar') }}
      </v-btn>
    </v-card>
  </div>
</template>

<script>
import ApiService from '../../../../../services/ApiService'
import Utils from '../../../../../Utils/FormatDate'
import SignatureScreen from '../../../../../components/SignatureScreen.vue'

export default {
  name: 'ManageRoutinePage',

  components: {
    SignatureScreen,
  },

  created() {
    this.checklistId = this.$route.query.id
    this.user = JSON.parse(localStorage.getItem('user'))

    if (this.checklistId) {
      this.getChecklist()
    }
  },

  data() {
    return {
      executionDateTimeError: false,
      executionDate: null,
      executionDateFormatted: '',
      menuExecutionDate: false,
      user: {},
      option: 0,
      signatureConfirmDialog: false,
      checklistId: '',
      headers: [
        {
          text: '',
          align: 'center',
          value: '',
          sortable: false,
        },
        {
          text: 'Item',
          align: 'center',
          value: 'item.name',
          sortable: false,
        },
        {
          text: 'Classe',
          align: 'center',
          value: 'item.itemClass.name',
          sortable: false,
        },
        {
          text: 'C',
          align: 'center',
          value: 'checklistItemYes',
          width: '50',
          sortable: false,
        },
        {
          text: 'NC',
          align: 'center',
          value: 'checklistItemNo',
          width: '50',
          sortable: false,
        },
        {
          text: 'N/A',
          align: 'center',
          value: 'checklistItemNotApplicaton',
          width: '50',
          sortable: false,
        },
        {
          text: 'Observação',
          align: 'center',
          value: 'observation',
          width: '300',
          sortable: false,
        },
      ],
      loading: false,
      apiService: new ApiService(),
      checklist: {
        answerTemplate: {},
      },
    }
  },

  methods: {
    parseDate(dateString) {
      if (!dateString || dateString.length < 10) return null

      const parts = dateString.split('/')
      if (parts.length !== 3) return null

      const day = parts[0]
      const month = parts[1]
      const year = parts[2]

      return `${year}-${month}-${day}`
    },

    formatDateToBR(dateString) {
      if (!dateString) return ''

      const parts = dateString.split('-')
      if (parts.length !== 3) return ''

      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },

    updateExecutionDateFromString() {
      if (
        this.executionDateFormatted &&
        this.executionDateFormatted.length === 10
      ) {
        this.executionDate = this.parseDate(this.executionDateFormatted)
        this.executionDateTimeError = false
      }
    },

    saveExecutionDate(date) {
      this.executionDateFormatted = this.formatDateToBR(date)
      this.menuExecutionDate = false
      this.executionDateTimeError = false
    },

    getFullExecutionDateTime() {
      if (!this.executionDate) return null

      const [year, month, day] = this.executionDate.split('-')

      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        0,
        0,
        0
      )
    },

    formatDate(date) {
      return Utils.dateTime(date)
    },

    openSignatureModal() {
      if (this.$refs.form.validate() && this.executionDate) {
        this.checklist.executionDate = this.getFullExecutionDateTime()

        const answers = this.checklist.answerTemplate.items.filter(
          (x) => x.response == null
        )

        if (answers && answers.length > 0) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
          this.$toast.warning(
            this.$t(
              'Existem itens sem resposta, verifique o formulário e tente novamente!'
            )
          )
          return
        }

        this.signatureConfirmDialog = true
      } else {
        this.executionDateTimeError = this.executionDate == null
      }
    },

    submit() {
      if (this.$refs.form.validate() && this.executionDate) {
        this.loading = true

        this.checklist.executionDate = this.getFullExecutionDateTime()

        this.apiService
          .post('inspectionRoutineChecklist/edit', this.checklist)
          .then(() => {
            this.$toast.success(this.$t('Checklist salvo com sucesso!'))
            this.$router.push(
              `/editServiceOrder/${this.checklist.serviceOrderId}`
            )
            this.loading = false
          })
          .finally(() => (this.loading = false))
      } else {
        this.executionDateTimeError = this.executionDate == null
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    async validateChecklist(data) {
      if (this.option == 0) {
        if (this.user.signatureImage) {
          this.checklist.isExecutingSigned = true
          this.checklist.executingUserId = this.user.id
          this.checklist.executorName = this.user.name
          this.checklist.executorImage = null

          this.submit()
          this.signatureConfirmDialog = false
        } else {
          this.signatureErrorDialog = true
        }
      } else if (this.option == 1) {
        this.checklist.isExecutingSigned = true
        this.checklist.executorImage = data
        this.checklist.executingUserId = this.user.id

        this.submit()
        this.signatureConfirmDialog = false
      } else {
        if (
          this.checklist &&
          this.checklist.serviceOrder &&
          this.checklist.serviceOrder &&
          this.checklist.serviceOrder.responsibleName
        ) {
          this.apiService
            .get(`byName/${this.checklist.serviceOrder.responsibleName}`)
            .then((response) => {
              this.checklist.executingUserId = response.content.id
              this.checklist.executorName = response.content.name
              this.checklist.executorImage = null

              this.submit()
              this.signatureConfirmDialog = false
            })
        } else {
          this.$toast.error(
            'Nenhum responsável encontrado na Ordem de Serviço!'
          )
        }
      }
    },

    getChecklist() {
      this.loading = true

      this.apiService
        .get(`inspectionRoutineChecklist/${this.checklistId}`)
        .then((response) => {
          this.checklist = response.content

          if (this.checklist.executionDate) {
            const date = new Date(this.checklist.executionDate)

            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            this.executionDate = `${year}-${month}-${day}`

            this.executionDateFormatted = `${day}/${month}/${year}`
          }

          if (
            this.checklist.answerTemplate.type == 1 &&
            this.checklist.answerTemplate.withoutEquipment == false
          ) {
            this.headers.splice(1, 0, {
              text: 'Equipamento',
              align: 'center',
              value: 'equipment.name',
              sortable: false,
            })
            this.headers.splice(1, 0, {
              text: 'Identificação',
              align: 'center',
              value: 'equipment.identification',
              sortable: false,
            })
          }
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.date-span-error {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}
</style>
